import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AlertModal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useAlert, useConfirm } from '../../hooks/useAdminUI';

import { AssessmentForm } from './AssessmentForm';

export const AssessmentManagement: React.FC = () => {
  const { workspace } = useWorkspace();
  const assessments = useQuery(api.assessments.list, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );

  useEffect(() => {
    document.title = 'LibreRank - Assessments';
  }, []);

  const createAssessment = useMutation(api.assessments.create);
  const updateAssessment = useMutation(api.assessments.update);
  const deleteAssessment = useMutation(api.assessments.remove);

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState<any>(null);

  // Modal state
  const { alert, showAlert, closeAlert } = useAlert();
  const { confirm, showConfirm, closeConfirm } = useConfirm<{ id: any; title: string }>();

  const handleCreateClick = () => {
    setEditingAssessment(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (assessment: any) => {
    setEditingAssessment(assessment);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: any) => {
    if (!workspace) return;

    try {
      if (editingAssessment) {
        await updateAssessment({
          id: editingAssessment._id,
          ...data
        });
        showAlert('Assessment Updated', `Successfully updated assessment: ${data.title}`, 'success');
      } else {
        await createAssessment({
          workspaceId: workspace._id,
          ...data
        });
        showAlert('Assessment Created', `Successfully created assessment: ${data.title}`, 'success');
      }
      setIsFormOpen(false);
      setEditingAssessment(null);
    } catch (err) {
      showAlert(
        editingAssessment ? 'Update Failed' : 'Creation Failed',
        err instanceof Error ? err.message : 'An unknown error occurred',
        'error'
      );
    }
  };

  const handleDeleteClick = (id: any, title: string) => {
    showConfirm("Delete Assessment", { id, title });
  };

  const handleConfirmDelete = async () => {
    if (confirm.data?.id) {
      try {
        await deleteAssessment({ id: confirm.data.id });
      } catch (err) {
        showAlert('Delete Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
      } finally {
        closeConfirm();
      }
    }
  };

  if (isFormOpen && workspace) {
    return (
      <WorkspaceAdminLayout title={editingAssessment ? "Edit Assessment" : "Create Assessment"}>
        <AssessmentForm 
          initialData={editingAssessment}
          workspaceId={workspace._id}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormOpen(false);
            setEditingAssessment(null);
          }}
          isSubmitting={false}
        />
      </WorkspaceAdminLayout>
    );
  }

  return (
    <WorkspaceAdminLayout title="Assessment Management">
      <AlertModal 
        isOpen={alert.isOpen} 
        onClose={closeAlert} 
        title={alert.title} 
        message={alert.message} 
        type={alert.type} 
      />
      
      <ConfirmModal 
        isOpen={confirm.isOpen}
        title="Delete Assessment"
        message={`Are you sure you want to delete "${confirm.data?.title}"? This will remove all associated assignments and submissions.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
        onCancel={closeConfirm}
      />

      <div className="flex justify-between items-center mb-8">
        <div>
          <p className="text-muted-foreground">
            Create and manage assessments for your workspace members.
          </p>
        </div>
        <button 
          onClick={handleCreateClick}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold shadow-sm"
        >
          Create Assessment
        </button>
      </div>

      <div className="grid gap-4">
        {!assessments ? (
          <div className="p-12 text-center bg-card border border-border rounded-xl">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-muted-foreground">Loading assessments...</p>
          </div>
        ) : assessments.length === 0 ? (
          <div className="p-16 text-center bg-card border border-border border-dashed rounded-xl">
            <div className="bg-muted w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              📋
            </div>
            <h3 className="text-lg font-medium mb-1">No assessments created</h3>
            <p className="text-muted-foreground mb-6">
              Create your first assessment to start evaluating candidates or students.
            </p>
            <button 
              onClick={handleCreateClick}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-semibold"
            >
              Create Your First Assessment
            </button>
          </div>
        ) : (
          assessments.map((a: Doc<"assessments">) => (
            <div key={a._id} className="bg-card p-6 rounded-xl border border-border flex justify-between items-center hover:shadow-md transition-shadow">
              <div>
                <h4 className="text-lg font-bold mb-1">{a.title}</h4>
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                    {a.problemSets.length} {a.problemSets.length === 1 ? 'Problem' : 'Problems'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-info"></span>
                    {a.timeLimitMinutes} mins
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                    {a.allowedSubmissions === -1 ? 'Unlimited' : `${a.allowedSubmissions} attempts`}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleEditClick(a)}
                  className="px-4 py-2 text-sm font-medium bg-transparent border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteClick(a._id, a.title)}
                  className="px-4 py-2 text-sm font-medium text-destructive border border-destructive/20 rounded-lg hover:bg-destructive/10 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </WorkspaceAdminLayout>
  );
};
